import React, { useState, useEffect } from 'react'
import axios from '~/data/http/axios'
import { useSelector, useDispatch } from 'react-redux'
import * as siteMaster from '~/data/actions/siteActions'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import Dialog from '~/Common-Objects/UI/Dialog'
import Hint from '~/Common-Objects/UI/Hint/Hint'
import Icons from '~/Common-Objects/UI/Icons/index'
import NewUserForm from '~/Common-Objects/NewUserForm/newUserForm.jsx'

/** Lab name → accountMaster/tier feature name when they differ */
const LAB_TIER_FEATURES = {
    'transpond-mcp': 'mcp'
}

function tierFeatureForLab(labName) {
    return LAB_TIER_FEATURES[labName] || labName
}

export function EnableFeature({
    labId = 0,
    onClose = () => {},
    includeLearnMore = false,
    inline = false,
    openRedirect = true
}) {
    const dispatch = useDispatch()

    const [lab, setLab] = useState({})
    const [planAllowed, setPlanAllowed] = useState(null)
    const [requiredTierName, setRequiredTierName] = useState(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        getLab()
    }, [])

    const getLab = async () => {
        try {
            const labData = await axios.get('/accountmaster/labs/' + labId)
            const nextLab = labData.data
            setLab(nextLab)

            const isPlanGated = !!nextLab.paid || !!LAB_TIER_FEATURES[nextLab.name]
            if (isPlanGated) {
                const feature = tierFeatureForLab(nextLab.name)
                try {
                    const [tierRes, requiredRes] = await Promise.all([
                        axios.get(`/accountMaster/tier/${feature}`),
                        axios.get(`/accountMaster/plan/requiredTier?feature=${feature}`).catch(() => null)
                    ])
                    setPlanAllowed(!!tierRes.data?.allowed)
                    setRequiredTierName(requiredRes?.data?.name || null)
                } catch (err) {
                    // Unknown / non-gated paid lab: keep the "on your plan" copy
                    setPlanAllowed(true)
                }
            } else {
                setPlanAllowed(true)
            }
        } catch (err) {
            setPlanAllowed(true)
        } finally {
            setLoading(false)
        }
    }

    const activateBeta = async () => {
        try {
            const res = await axios.put('/accountmaster/labs', { betaId: labId })
            if (openRedirect && res.data.redirect) {
                window.open(res.data.redirect, '_blank')
            }
            onClose(true)
        } catch (err) {
            onClose(false)
        }
    }

    const activateAccount = () => {
        dispatch(
            siteMaster.alterFullpage(
                <NewUserForm
                    from={'labs-' + lab.name}
                    finish={() => {
                        dispatch(siteMaster.alterFullpage(undefined))
                    }}
                />
            )
        )
        onClose(false)
    }
    if (loading) return <></>

    if (inline) {
        return (
            <EnableFeatureContent
                lab={lab}
                planAllowed={planAllowed}
                requiredTierName={requiredTierName}
                activateBeta={activateBeta}
                activateAccount={activateAccount}
                includeLearnMore={includeLearnMore}
                onClose={onClose}
            />
        )
    }

    return (
        <Dialog.Root open={true}>
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content>
                    <EnableFeatureContent
                        lab={lab}
                        planAllowed={planAllowed}
                        requiredTierName={requiredTierName}
                        activateBeta={activateBeta}
                        activateAccount={activateAccount}
                        includeLearnMore={includeLearnMore}
                        onClose={onClose}
                    />
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    )
}

function planAccessCopy(lab, planAllowed, requiredTierName) {
    const isPlanGated = !!lab.paid || !!LAB_TIER_FEATURES[lab.name]
    if (!isPlanGated) {
        return ` It's completely free to try this beta, but in the future this might become a paid only option.`
    }
    if (planAllowed === false) {
        const tierHint = requiredTierName ? ` (${requiredTierName} or higher)` : ''
        return ` This beta isn't included on your current plan${tierHint}. You can turn it on now, but you'll need to upgrade before you can use it.`
    }
    return ` It's free to try on your current plan, but may move to a higher tier in the future.`
}

export function EnableFeatureContent({
    lab,
    planAllowed = true,
    requiredTierName = null,
    onClose = () => {},
    activateAccount = () => {},
    activateBeta = () => {},
    includeLearnMore = false
}) {
    const account = useSelector((state) => state.accountMaster)
    return (
        <div style={{ padding: '40px' }}>
            <h4 className='mar-b10'>Enable {lab.fullName}</h4>
            <div className='mar-b10'>
                This is a beta feature, so functionality may change and occasional bugs are possible.
                {planAccessCopy(lab, planAllowed, requiredTierName)}
            </div>
            <div className='mar-b25'>Enabling it now will activate the feature for all users in your account.</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    {!!account.accountMaster.approved && (
                        <Button
                            className='button-primary mar-r10'
                            text={"That's fine, let me try it"}
                            onClick={activateBeta}
                        />
                    )}
                    {!account.accountMaster.doneNewUserQuestions && !account.accountMaster.approved && (
                        <Button className='button-orange mar-r10' onClick={activateAccount}>
                            Request Approval
                        </Button>
                    )}
                    {account.accountMaster.doneNewUserQuestions && !account.accountMaster.approved && (
                        <Button className='button-primary mar-r10' disabled={true} onClick={activateAccount}>
                            Approval Pending
                        </Button>
                    )}
                    <Button
                        text='Cancel'
                        onClick={() => {
                            onClose(false)
                        }}
                    />
                </div>
                {includeLearnMore && lab.helpLink && lab.helpLink.length > 5 && (
                    <div>
                        <Hint
                            onClick={() => {
                                window.open(lab.helpLink, '_blank')
                            }}
                            iconLeft={
                                <Icons icon='questionCircle' color='#2B2F41' style={{ height: 16, width: 16 }} />
                            }
                        >
                            Learn More
                        </Hint>
                    </div>
                )}
            </div>
        </div>
    )
}
